import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";

import ROUTES from "../routes";
import api from "./../config/axiosInstance";
import { handleErrors } from "./../utils/common";

function Dashboard() {
    const [dashboard, setDashboardCounts] = useState({});
    const [ordersGraph, setOrdersGraph] = useState([]);
    const [categoriesGraph, setCategoriesGraph] = useState([]);
    const [brandsGraph, setBrandsGraph] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getCounts = async () => {
            setLoading(true);
            try {
                const [counts, orders, categories, brands] = await Promise.all([
                    api.get('/dashboard/counts'),
                    api.get('/dashboard/graphs/orders'),
                    api.get('/dashboard/graphs/categories'),
                    api.get('/dashboard/graphs/brands'),
                ]);

                if (counts.status === 200) setDashboardCounts(counts.data.dashboard || {});
                if (orders.status === 200) setOrdersGraph(orders.data.graph || []);
                if (categories.status === 200) setCategoriesGraph(categories.data.graph || []);
                if (brands.status === 200) setBrandsGraph(brands.data.graph || []);
            } catch (err) {
                handleErrors(err);
            } finally {
                setLoading(false);
            }
        };

        getCounts();
    }, []);

    const ordersChartOptions = {
        chart: { type: 'area', height: 350, toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth' },
        xaxis: { categories: ordersGraph.map(item => item._id) },
    };

    const ordersChartSeries = [
        { name: 'Revenue', data: ordersGraph.map(item => item.revenue) },
        { name: 'Orders Count', data: ordersGraph.map(item => item.count) }
    ];

    // Many categories don't fit a pie chart, so use a horizontal bar chart
    // sorted by count with a height that grows with the number of categories.
    const sortedCategories = [...categoriesGraph].sort((a, b) => (b?.count || 0) - (a?.count || 0));
    const categoriesChartHeight = Math.max(350, sortedCategories.length * 32);
    const categoriesChartOptions = {
        chart: { type: 'bar', height: categoriesChartHeight, toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: '70%' } },
        dataLabels: { enabled: true },
        xaxis: { categories: sortedCategories.map(item => item?.title || 'N/A'), title: { text: 'Products' } }
    };
    const categoriesChartSeries = [
        { name: 'Products', data: sortedCategories.map(item => item?.count || 0) }
    ];

    const brandsChartOptions = {
        chart: { type: 'bar', height: 350, toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        dataLabels: { enabled: false },
        xaxis: { categories: brandsGraph.map(item => item?.title || 'N/A') }
    };
    const brandsChartSeries = [
        { name: 'Products', data: brandsGraph.map(item => item?.count || 0) }
    ];

    const cards = [
        {
            key: 'brand',
            label: 'Total Brands',
            route: ROUTES.BRANDS,
            icon: 'fa-solid fa-tags',
        },
        {
            key: 'category',
            label: 'Total Categories',
            route: ROUTES.CATEGORIES,
            icon: 'fa-solid fa-list',
        },
        {
            key: 'product',
            label: 'Total Products',
            route: ROUTES.PRODUCTS,
            icon: 'fa-solid fa-box',
        },
        {
            key: 'blogs',
            label: 'Total Blogs',
            route: ROUTES.BLOGS,
            icon: 'fa-solid fa-newspaper',
        },
    ];

    return (
        <>
            <div className="form-head d-md-flex mb-sm-4 mb-3 align-items-start">
                <div className="me-auto d-lg-block">
                    <h2 className="text-black font-w600">Dashboard</h2>
                    <p className="mb-0">Welcome to Prime Autoz</p>
                </div>
            </div>

            <div className="row">
                {cards.map(({ key, label, route, icon }) => (
                    <div className="col-xl-3 col-md-6" key={key}>
                        <div className="card">
                            <div className="card-body">
                                <Link to={route} className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h3>
                                            {loading ? (
                                                <span className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </span>
                                            ) : (
                                                dashboard[key] ?? 0
                                            )}
                                        </h3>
                                        <span className="fs-14 text-black">{label}</span>
                                    </div>
                                    <span className="border border-primary text-primary rounded p-3">
                                        <i className={`${icon} fa-2x`}></i>
                                    </span>
                                </Link>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row mt-4">

                <div className="col-xl-12 col-lg-12">
                    <div className="card">
                        <div className="card-header border-0 pb-0">
                            <h4 className="fs-20 mb-0">Products per Category</h4>
                        </div>
                        <div className="card-body">
                            {!loading && (
                                <ReactApexChart options={categoriesChartOptions} series={categoriesChartSeries} type="bar" height={categoriesChartHeight} />
                            )}
                        </div>
                    </div>
                </div>


                <div className="col-xl-6 col-lg-12">
                    <div className="card">
                        <div className="card-header border-0 pb-0">
                            <h4 className="fs-20 mb-0">Orders & Revenue</h4>
                        </div>
                        <div className="card-body">
                            {!loading && (
                                <ReactApexChart options={ordersChartOptions} series={ordersChartSeries} type="area" height={350} />
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-xl-6 col-lg-12 mt-4">
                    <div className="card">
                        <div className="card-header border-0 pb-0">
                            <h4 className="fs-20 mb-0">Products per Brand</h4>
                        </div>
                        <div className="card-body">
                            {!loading && (
                                <ReactApexChart options={brandsChartOptions} series={brandsChartSeries} type="bar" height={350} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Dashboard;
